import type { ReactElement } from 'react';
import { useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import { Modal, Stack, Text } from '@hotjar/hotsauce';
import type { GlobalModalProps } from '@hotjar/webapp/features/GlobalModal/createGlobalModal';
import { createGlobalModal } from '@hotjar/webapp/features/GlobalModal/createGlobalModal';
import { vatAppliesToCountry } from '@hotjar/webapp/billing/tax';
import type { CountryCode } from '@hotjar/country-lib';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { BILLING_CYCLE_EVENT } from '@hotjar/checkout/src/utils/tracking';
import { trackClick } from '@hotjar/webapp/tracking/common';
import { extractAccountGroupingProps } from '@hotjar/webapp/tracking/utils';
import { BillingCycleSelector } from './components/BillingCycleSelector';
import { CreditCardDetails } from './CreditCardDetails/CreditCardDetails';
import type { PaymentDetailsProps } from './types';
import { useDisplayCosts } from './utils/useDisplayCosts';

export interface OnSuccessResponse {
  refId: string;
  billingCycle: IBillingCycle;
}

export const PaymentDetails = ({
  account,
  costSpecs,
  initialBillingCycle,
  hideBillingCycleSelector,
  customerId,
  country,
  onCancel,
  onSubmit,
}: PaymentDetailsProps & GlobalModalProps<OnSuccessResponse, string>): ReactElement => {
  const { t } = useTranslationWithPrefix('billing:payment-details');

  // wait, why is there a useState and useRef to track billingCycle??? I'm glad you asked...
  // CreditCardDetails is a stateless components that won't update even if its callback functions
  // change. We need to access the updated billingCycle value inside onSuccess with the refId it passes from
  // CreditCardDetails. So the work-around was to save billingCycle to both useState (update the rest of the component)
  // and useRef (get the updated value for billingCycle inside onSuccess callback)
  const [billingCycle, setBillingCycle] = useState<IBillingCycle>(initialBillingCycle ?? 'monthly');
  const billingCycleRef = useRef<IBillingCycle>(initialBillingCycle ?? 'monthly');

  const { monthlyCosts, yearlyCosts, disclaimerDisplayCosts } = useDisplayCosts(costSpecs);

  const countryCode = country ?? (account?.location?.split(',')[1] as CountryCode);
  const exclVatText = vatAppliesToCountry(countryCode) ? t('legal-text.excl-tax') : '';

  const showBillingCycleSelector =
    !hideBillingCycleSelector && (initialBillingCycle === 'monthly' || initialBillingCycle == null);

  const close = () => onCancel();
  const handleSuccess = ({ refId }: { refId: string }) =>
    onSubmit({ refId, billingCycle: billingCycleRef.current });
  const handleError = ({ errorMessage }: { errorMessage: string }) => onCancel(errorMessage);
  const updateBillingCycle = (state: IBillingCycle) => {
    // trigger didComponentUpdate
    setBillingCycle(state);
    // save a ref of the value to use inside handleSuccess
    billingCycleRef.current = state;
    trackClick({
      name: BILLING_CYCLE_EVENT,
      uiPosition: state,
      groupData: extractAccountGroupingProps(account),
    });
  };

  return (
    <Modal contentLabel={t('title')} isOpen onClose={close}>
      <Modal.Header title={t('title')} />
      <Modal.Body>
        <Stack preset="form">
          {showBillingCycleSelector && (
            <>
              <Text textType="subtitle1">{t('billing-cycle.title')}</Text>
              <BillingCycleSelector
                yearlyAmounts={yearlyCosts as IBillingAmounts}
                monthlyAmounts={monthlyCosts as IBillingAmounts}
                billingCycle={billingCycle}
                onChange={updateBillingCycle}
              />
            </>
          )}

          <CreditCardDetails
            customerId={customerId}
            countryCode={countryCode}
            onSuccess={handleSuccess}
            onError={handleError}
          />
          {billingCycle === 'yearly_recurring' && disclaimerDisplayCosts?.isVisible && (
            <Text textType="body2">
              {disclaimerDisplayCosts?.totalFuture ? (
                <Trans
                  i18nKey="billing:payment-details.legal-text.prorated"
                  values={{
                    currentTotal: disclaimerDisplayCosts.totalCurrent,
                    futureTotal: disclaimerDisplayCosts.totalFuture,
                  }}
                  components={[<span>{exclVatText}</span>]}
                />
              ) : (
                <Trans
                  i18nKey="billing:payment-details.legal-text.not-prorated"
                  values={{
                    currentTotal: disclaimerDisplayCosts?.totalCurrent,
                  }}
                  components={[<span>{exclVatText}</span>]}
                />
              )}
            </Text>
          )}
        </Stack>
      </Modal.Body>
    </Modal>
  );
};

export const openPaymentDetailsModal = createGlobalModal<
  PaymentDetailsProps,
  OnSuccessResponse,
  string
>(PaymentDetails);
